import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';

import { getStockBasketDetail } from '../api';
import { useColorModeValue } from '../components/ui/color-mode';
import { isRecoverableAuthError, getLoginRedirectOptions } from '../utils/auth0Errors';

const FINANCIAL_ROWS = [
  { label: 'Market Cap', key: 'marketCap' },
  { label: 'Enterprise Value', key: 'enterpriseValue' },
  { label: 'Revenue', key: 'revenue' },
  { label: 'Net Income', key: 'netIncome' },
  { label: 'Free Cash Flow', key: 'freeCashFlow' },
  { label: 'Cash', key: 'cash' },
  { label: 'Total Debt', key: 'totalDebt' },
  { label: 'Net Debt', key: 'netDebt' },
];

const RATIO_ROWS = [
  { label: 'Price / Sales', key: 'priceToSales', formatter: formatMultiple },
  { label: 'Price / Earnings', key: 'priceToEarnings', formatter: formatMultiple },
  { label: 'Price / Free Cash Flow', key: 'priceToFreeCashFlow', formatter: formatMultiple },
  { label: 'EV / Sales', key: 'evToSales', formatter: formatMultiple },
  { label: 'EV / FCF', key: 'evToFcf', formatter: formatMultiple },
  { label: 'FCF Margin', key: 'fcfMargin', formatter: formatPercent },
];

function formatCurrency(value) {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const formatAbbreviated = (number, suffix = '') => {
    const fixed = number >= 10 ? number.toFixed(1) : number.toFixed(2);
    return `${sign}$${parseFloat(fixed).toString()}${suffix}`;
  };

  if (abs >= 1e12) return formatAbbreviated(abs / 1e12, 'T');
  if (abs >= 1e9) return formatAbbreviated(abs / 1e9, 'B');
  if (abs >= 1e6) return formatAbbreviated(abs / 1e6, 'M');
  if (abs >= 1e3) return formatAbbreviated(abs / 1e3, 'K');

  return `${sign}$${abs.toLocaleString()}`;
}

function formatMultiple(value) {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  const fixed = value >= 10 ? value.toFixed(1) : value.toFixed(2);
  return `${parseFloat(fixed).toString()}x`;
}

function formatPercent(value) {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${parseFloat((value * 100).toFixed(1)).toString()}%`;
}

function SectionCard({ title, description, children, bg, borderColor, mutedText }) {
  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      p={{ base: 5, md: 6 }}
      boxShadow="sm"
    >
      <Stack gap={4}>
        <Box>
          <Heading as="h2" size="md">
            {title}
          </Heading>
          {description ? (
            <Text mt={2} color={mutedText} fontSize="sm">
              {description}
            </Text>
          ) : null}
        </Box>
        {children}
      </Stack>
    </Box>
  );
}

function MetricRowsTable({ rows, data, emptyState, headerBg, headerTextColor }) {
  return (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
      <Table.Root variant="simple" size="sm">
        <Table.Header bg={headerBg}>
          <Table.Row>
            <Table.ColumnHeader color={headerTextColor}>Metric</Table.ColumnHeader>
            <Table.ColumnHeader color={headerTextColor}>Value</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <Table.Row key={row.key} bg={index % 2 === 0 ? 'transparent' : headerBg}>
                <Table.Cell fontWeight="medium">{row.label}</Table.Cell>
                <Table.Cell>{row.formatter(data[row.key])}</Table.Cell>
              </Table.Row>
            ))
          ) : (
            <Table.Row>
              <Table.Cell colSpan={2}>{emptyState}</Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

function BackToStockBasketsLink() {
  return (
    <Link
      as={RouterLink}
      to="/stock-baskets"
      color="teal.500"
      fontWeight="semibold"
      display="inline-flex"
      alignItems="center"
      columnGap={2}
      textDecoration="none"
      _hover={{ textDecoration: 'underline' }}
    >
      <Box as="span" display="inline-flex" flexShrink={0} aria-hidden>
        <FiArrowLeft size={18} strokeWidth={2.25} />
      </Box>
      Back to all stock baskets
    </Link>
  );
}

function StockBasketDetail() {
  const { basketId } = useParams();
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const loginWithRedirectRef = useRef(loginWithRedirect);
  loginWithRedirectRef.current = loginWithRedirect;
  const [basket, setBasket] = useState(null);
  const [error, setError] = useState('');
  const [loadingBasket, setLoadingBasket] = useState(true);

  const pageBgStart = useColorModeValue('gray.50', 'gray.950');
  const pageBgEnd = useColorModeValue('teal.100', 'gray.900');
  const panelBg = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const headerBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const headerTextColor = useColorModeValue('gray.700', 'gray.200');
  const tickerPillBg = useColorModeValue('teal.50', 'teal.900');
  const tickerPillBorder = useColorModeValue('teal.200', 'teal.700');
  const computedTotals = basket?.computed?.totals || {};
  const computedRatios = basket?.computed?.ratios || {};
  const computedCompanies = basket?.computed?.companies || [];
  const computedByTicker = Object.fromEntries(
    computedCompanies.map((company) => [company.ticker, company]),
  );

  useEffect(() => {
    let cancelled = false;

    async function loadBasket() {
      setLoadingBasket(true);
      setError('');

      try {
        let token;
        if (isAuthenticated) {
          token = await getAccessTokenSilently();
        }

        const data = await getStockBasketDetail(basketId, token);
        if (!cancelled) {
          setBasket(data);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (isRecoverableAuthError(err)) {
          await loginWithRedirectRef.current(getLoginRedirectOptions());
          return;
        }
        setBasket(null);
        setError(err.message || 'Failed to load basket');
      } finally {
        if (!cancelled) {
          setLoadingBasket(false);
        }
      }
    }

    if (!isLoading) {
      void loadBasket();
    }

    return () => {
      cancelled = true;
    };
  }, [basketId, getAccessTokenSilently, isAuthenticated, isLoading]);

  if (loadingBasket) {
    return (
      <Box
        minH="calc(100vh - var(--nav-h))"
        bgGradient="to-b"
        gradientFrom={pageBgStart}
        gradientTo={pageBgEnd}
      >
        <Flex justify="center" align="center" py={20}>
          <Spinner size="lg" color="teal.500" />
        </Flex>
      </Box>
    );
  }

  if (!basket) {
    return (
      <Box
        minH="calc(100vh - var(--nav-h))"
        bgGradient="to-b"
        gradientFrom={pageBgStart}
        gradientTo={pageBgEnd}
      >
        <Box maxW="900px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 10 }}>
          <SectionCard
            title={error === 'Forbidden' ? 'Basket unavailable' : 'Basket not found'}
            description={
              error === 'Forbidden'
                ? 'This basket is private and not accessible from your current session.'
                : 'That basket id does not match an available stock basket.'
            }
            bg={panelBg}
            borderColor={borderColor}
            mutedText={mutedText}
          >
            {error ? (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>{error}</Alert.Content>
              </Alert.Root>
            ) : null}
            <BackToStockBasketsLink />
          </SectionCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minH="calc(100vh - var(--nav-h))"
      bgGradient="to-b"
      gradientFrom={pageBgStart}
      gradientTo={pageBgEnd}
    >
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 10 }}>
        <Stack gap={8}>
          <Box>
            <BackToStockBasketsLink />
            <Heading as="h1" mt={5} fontSize={{ base: '3xl', md: '5xl' }} lineHeight="1.05">
              {basket.title}
            </Heading>
            <Text mt={4} maxW="720px" fontSize={{ base: 'md', md: 'lg' }} color={mutedText}>
              {basket.description || 'No description yet.'}
            </Text>
          </Box>

          <SectionCard
            title="Basket members"
            description="The basket stores tickers in order and uses company metadata from company_tickers for display."
            bg={panelBg}
            borderColor={borderColor}
            mutedText={mutedText}
          >
            {basket.members.length > 0 ? (
              <Flex wrap="wrap" gap={3}>
                {basket.members.map((member) => (
                  <Flex
                    key={member.ticker}
                    align="center"
                    gap={3}
                    px={4}
                    py={2.5}
                    bg={tickerPillBg}
                    borderWidth="1px"
                    borderColor={tickerPillBorder}
                    borderRadius="full"
                  >
                    <Stack gap={0.5}>
                      <Text fontWeight="semibold" lineHeight="shorter">
                        {member.ticker}
                      </Text>
                      <Text fontSize="xs" color={mutedText} lineHeight="short">
                        {member.name || 'Unknown company'}
                      </Text>
                    </Stack>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Text color={mutedText}>No companies in this basket yet.</Text>
            )}

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={4}>
                <Stack gap={1}>
                  <Text fontSize="sm" color={mutedText}>
                    Companies
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="shorter">
                    {basket.tickers.length}
                  </Text>
                </Stack>
              </Box>
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={4}>
                <Stack gap={1}>
                  <Text fontSize="sm" color={mutedText}>
                    Combined Market Cap
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="shorter">
                    {formatCurrency(computedTotals.marketCap)}
                  </Text>
                </Stack>
              </Box>
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={4}>
                <Stack gap={1}>
                  <Text fontSize="sm" color={mutedText}>
                    Combined Revenue
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="shorter">
                    {formatCurrency(computedTotals.revenue)}
                  </Text>
                </Stack>
              </Box>
            </SimpleGrid>
          </SectionCard>

          <SectionCard
            title="Company-level table"
            description="Rows come from the basket membership table plus ticker metadata."
            bg={panelBg}
            borderColor={borderColor}
            mutedText={mutedText}
          >
            <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
              <Box overflowX="auto">
                <Table.Root variant="simple" size="sm">
                  <Table.Header bg={headerBg}>
                    <Table.Row>
                      <Table.ColumnHeader color={headerTextColor}>Ticker</Table.ColumnHeader>
                      <Table.ColumnHeader color={headerTextColor}>Company</Table.ColumnHeader>
                      <Table.ColumnHeader color={headerTextColor}>Sector</Table.ColumnHeader>
                      <Table.ColumnHeader color={headerTextColor}>Market Cap</Table.ColumnHeader>
                      <Table.ColumnHeader color={headerTextColor}>Revenue</Table.ColumnHeader>
                      <Table.ColumnHeader color={headerTextColor}>FCF</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {basket.members.length > 0 ? (
                      basket.members.map((member, index) => {
                        const companyFinancials = computedByTicker[member.ticker] || {};
                        return (
                          <Table.Row key={member.ticker} bg={index % 2 === 0 ? 'transparent' : headerBg}>
                            <Table.Cell fontWeight="semibold">{member.ticker}</Table.Cell>
                            <Table.Cell>{member.name || companyFinancials.name || 'Unknown company'}</Table.Cell>
                            <Table.Cell>{member.sector || 'N/A'}</Table.Cell>
                            <Table.Cell>{formatCurrency(companyFinancials.marketCap)}</Table.Cell>
                            <Table.Cell>{formatCurrency(companyFinancials.revenue)}</Table.Cell>
                            <Table.Cell>{formatCurrency(companyFinancials.freeCashFlow)}</Table.Cell>
                          </Table.Row>
                        );
                      })
                    ) : (
                      <Table.Row>
                        <Table.Cell colSpan={6} color={mutedText}>
                          No basket members are available yet.
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
            </Box>
          </SectionCard>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap={6}>
            <SectionCard
              title="Combined financials"
              description="Totals are computed from the latest available market cap plus annual SEC 10-K financial data."
              bg={panelBg}
              borderColor={borderColor}
              mutedText={mutedText}
            >
              <MetricRowsTable
                rows={FINANCIAL_ROWS.map((row) => ({
                  ...row,
                  formatter: formatCurrency,
                }))}
                data={computedTotals}
                headerBg={headerBg}
                headerTextColor={headerTextColor}
                emptyState="No basket totals available yet."
              />
            </SectionCard>

            <SectionCard
              title="Valuation ratios"
              description="Ratios are based on the basket-level totals and show N/A when underlying data is missing."
              bg={panelBg}
              borderColor={borderColor}
              mutedText={mutedText}
            >
              <MetricRowsTable
                rows={RATIO_ROWS}
                data={computedRatios}
                headerBg={headerBg}
                headerTextColor={headerTextColor}
                emptyState="No basket ratios available yet."
              />
            </SectionCard>
          </SimpleGrid>
        </Stack>
      </Box>
    </Box>
  );
}

export default StockBasketDetail;
